import { ChangeDetectionStrategy, Component } from '@angular/core';
import { splitCouchSeatNumber } from '@common/helper';
import { SeatInfo, SeatInfoPassengers, SeatNumbers } from '@common/interfaces/servicing-seatmap/seat-info';
import { PassengerForServicingSeatmapScreen } from '@common/interfaces/servicing-seatmap/passenger-for-seatmap-screen';
import { SeatForServicingSeatmapScreen } from '@common/interfaces/servicing-seatmap/seatmap-for-seatmap-screen';
import { ServicingSeatmapStoreService } from '@common/services/seatmap/servicing-seatmap-store.service';
import { SupportModalBlockComponent } from '@lib/components/support-class';
import { AswMasterService, CommonLibService, DialogDisplayService } from '@lib/services';
import { DialogClickType, DialogSize, DialogType, PageType } from '@lib/interfaces';
import { GetOrderResponseData, GetSeatmapsResponse, OrderSeatItemCouchCatalogue, Type1 } from 'src/sdk-servicing';
import { Router } from '@angular/router';
import { SeatmapStoreModel } from '@common/interfaces/servicing-seatmap/seatmap-store-model';
import { take, zip } from 'rxjs';
import { MasterJsonKeyPrefix, MasterStoreKey } from '@conf/asw-master.config';
import { StaticMsgPipe } from '@lib/pipes';
import { ServicingCommonStoreService } from '@common/services';

const SEATMAP_PROPETRY_CATEGORY = 'seatMap';

@Component({
  selector: 'asw-servicing-seatmap-paid-seat-detail-confirmation',
  templateUrl: './servicing-seatmap-paid-seat-detail-confirmation.component.html',
  styleUrls: ['./servicing-seatmap-paid-seat-detail-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServicingSeatmapPaidSeatDetailConfirmationComponent extends SupportModalBlockComponent {
  /** 座席情報マップ */
  seatInformationMap?: Map<string, SeatForServicingSeatmapScreen>;

  /** 搭乗者マップ */
  travelersMap?: Map<string, PassengerForServicingSeatmapScreen>;

  /** 搭乗者IDリスト */
  passengerIdList: string[] = [];

  /** 全セグメント情報 */
  allSegmentInfo?: Array<Type1>;

  /** 表示対象セグメントID */
  displayTargetSegmentId?: string;

  /** 選択中座席情報リスト */
  selectedSeatInfoList: SeatInfo[] = [];

  /** PNR情報 */
  pnrInfo?: GetOrderResponseData;

  /** シートマップ情報 */
  seatmapInfo?: GetSeatmapsResponse;

  /** 座席番号 */
  seatNumber?: string;

  /** カウチ座席番号 */
  couchSeatNumber?: string;

  /** 選択中搭乗者ID */
  selectingPassengerId?: string;

  /** エラーメッセージID */
  errorMessageId?: string;

  /** カウチ席指定搭乗者IDリスト */
  couchSeatAppliedPassengerList = new Map<string, boolean>();

  /** 選択中の搭乗者 (表示用) */
  selectingPassenger?: PassengerForServicingSeatmapScreen;

  /** カウチ席指定中人数 */
  couchSeatReservedPassengerCount: number = 0;

  /** カウチ金額情報 */
  couchAmountInfo?: OrderSeatItemCouchCatalogue;

  /** カウチ席指定料金合計金額 */
  reservedCouchSeatTotalAmount: number | undefined = 0;

  /** シートマップ（参照）かどうか */
  isInformative: boolean = false;

  /** カウチ席かどうか */
  isCouchSeat?: boolean;

  /** 通常席の画像 */
  chargeASRSeatImage?: string;

  /** カウチ席の画像 */
  couchSeatImage?: string;

  /** 通常席のシート画像alt文言のための静的文言キー */
  chargeASRSeatImgAltStaticMsgKey: string = '';

  /** 請求案内 */
  chargableDescription: string = '';

  /** 適用条件 */
  chargableAttention: string = '';

  /** 座席変更不可かどうか */
  isChangeRestrictedAllSeat?: boolean;

  private readonly CHARGABLE_DESCRIPTION_KEY: Record<string, string> = {
    E: 'message.chargableDescription.E',
    A: 'message.chargableDescription.A',
    CH: 'message.chargableDescription.CH',
    L: 'message.chargableDescription.L',
    W: 'message.chargableDescription.W',
    WA: 'message.chargableDescription.WA',
  };

  private readonly CHARGABLE_ATTENTION_KEY: Record<string, string> = {
    E: 'message.chargableAttention.E',
    A: 'message.chargableAttention.A',
    CH: 'message.chargableAttention.CH',
    L: 'message.chargableAttention.L',
    W: 'message.chargableAttention.W',
    WA: 'message.chargableAttention.WA',
  };

  override reload(): void {}
  override init(): void {
    this.subscribeService(
      'ServicingSeatmapPaidSeatDetailConfirmationModalComponent-init',
      zip([
        this._servicingSeatmapStoreService.getServicingSeatmapModel$(),
        this._masterService.getAswMasterByKey$(MasterStoreKey.AIRCRAFT_CABIN_I18NJOIN_ACV_AND_CABIN).pipe(take(1)),
      ]),
      ([state, mAircraftCabin]) => {
        if (this._isDisplay(state)) {
          this.allSegmentInfo = state.segments;
          this.seatInformationMap = new Map<string, SeatForServicingSeatmapScreen>(Object.entries(state.seatmap!));
          this.seatNumber = state.seatNumber!;
          this.seatmapInfo = state.seatmapInfo;
          this.isChangeRestrictedAllSeat = this.seatmapInfo?.data.travelersSummary.isChangeRestrictedAllSeat;

          if (this.seatInformationMap.get(this.seatNumber)?.isCouchSeat) {
            this.couchSeatNumber = this.seatInformationMap.get(this.seatNumber)?.couchNumber as string;
          }

          // シートマップの場合
          if (!this.isInformative) {
            this.travelersMap = new Map<string, PassengerForServicingSeatmapScreen>(Object.entries(state.passengers!));
            this.passengerIdList = state.passengerList!;
            this.displayTargetSegmentId = this._servicingCommonStoreService.servicingCommon.displayTargetSegmentId;
            this.selectedSeatInfoList = state.selectedSeatInfoList!;
            this.pnrInfo = state.pnrInfo;
            this.selectingPassengerId = state.selectingPassengerId;
            this.passengerIdList.forEach((id) => {
              let isChecked = false;
              let tempCouchSeat = this.selectedSeatInfoList
                .find((s) => s.segmentId === this.displayTargetSegmentId)
                ?.passengerList.find((p) => p.id === id)?.couchSeatNumberList;
              if (
                !!this.couchSeatNumber &&
                !!tempCouchSeat &&
                tempCouchSeat.length > 0 &&
                tempCouchSeat[0].seatNumber === this.couchSeatNumber
              ) {
                isChecked = true;
              }
              this.couchSeatAppliedPassengerList?.set(id, isChecked);
            });
          }

          if (this.seatNumber) {
            // カウチ席かどうか
            this.isCouchSeat = this.seatInformationMap.get(this.seatNumber)?.isCouchSeat || false;
          }

          // カウチ席の場合
          if (this.isCouchSeat) {
            // 指定中人数を設定
            this.couchSeatReservedPassengerCount = 0;
            this.couchSeatAppliedPassengerList?.forEach((value) => {
              if (value) {
                this.couchSeatReservedPassengerCount! += 1;
              }
            });

            // 合計金額を設定
            this.reservedCouchSeatTotalAmount = this.getSeatPrice();
          }

          const contentsServerUrl = this._masterService.getMPropertyByKey(
            SEATMAP_PROPETRY_CATEGORY,
            'contents.server.url'
          );

          let displayTargetSegment = this.allSegmentInfo?.find((seg) => seg.id === this.displayTargetSegmentId);
          if (displayTargetSegment) {
            // キャビン別機材情報マスタから対象のレコードを取得
            const version = displayTargetSegment.aircraftConfigurationVersion;
            const cabinName = displayTargetSegment.cabin ? displayTargetSegment.cabin : undefined;
            if (!!version && !!cabinName) {
              const targetCabinRecord = mAircraftCabin?.[version]?.[cabinName]?.[0];

              if (targetCabinRecord?.seat_type_image) {
                // マスタから通常席のシート画像を取得
                this.chargeASRSeatImage = `${contentsServerUrl}${targetCabinRecord.seat_type_image}.png`;

                // 通常席のシート画像alt文言のための静的文言キーを設定
                this.chargeASRSeatImgAltStaticMsgKey = 'alt.paidSeatsDetailModal.' + targetCabinRecord.seat_type_image;
              }
            }
          }

          // マスタからカウチ席のシート画像を取得
          const couchSeatImage = this._masterService.getMPropertyByKey(
            SEATMAP_PROPETRY_CATEGORY,
            'seatProduct.image.couch'
          );
          this.couchSeatImage = `${contentsServerUrl}${couchSeatImage}.png`;

          // 座席種別説明、請求案内を取得
          this._getSeatTypeInformation();
        }
      }
    );
    // URL変化時に自動でモーダルが閉じられるように修正
    this.closeWithUrlChange(this._router);
  }

  /**
   * 請求案内、注意案内、適用条件を取得
   */
  private _getSeatTypeInformation() {
    // 請求案内対象
    const dedicatedChargeableSeatPromotion = this._masterService.getMPropertyByKey(
      SEATMAP_PROPETRY_CATEGORY,
      'characteristic.dedicatedChargeableSeatPromotion'
    );
    const seatPromotion = dedicatedChargeableSeatPromotion.split('|');

    let seatCharacteristicsCodes: string[] | undefined;
    let seatCharacteristicsCode: string | undefined;
    if (this.seatNumber && this.seatInformationMap) {
      seatCharacteristicsCodes = this.seatInformationMap.get(this.seatNumber)?.seatCharacteristicsCodes;
      seatCharacteristicsCode = seatPromotion.find((code) => seatCharacteristicsCodes?.includes(code));
    }

    // 座席属性が存在しない場合、座席特性は「CH」とする。
    if (!seatCharacteristicsCode) {
      seatCharacteristicsCode = 'CH';
    }

    if (!!seatCharacteristicsCode) {
      let chargableDescriptionKey = this.CHARGABLE_DESCRIPTION_KEY[seatCharacteristicsCode];
      let chargableAttentionKey = this.CHARGABLE_ATTENTION_KEY[seatCharacteristicsCode];

      // 請求案内
      this.chargableDescription = !!chargableDescriptionKey
        ? this._staticMsgPipe.transform(chargableDescriptionKey)
        : '';
      // 適用条件
      this.chargableAttention = !!chargableAttentionKey ? this._staticMsgPipe.transform(chargableAttentionKey) : '';
    }
  }

  override destroy(): void {}

  constructor(
    private _common: CommonLibService,
    private _servicingSeatmapStoreService: ServicingSeatmapStoreService,
    private _dialogDisplayService: DialogDisplayService,
    private _router: Router,
    private _masterService: AswMasterService,
    private _staticMsgPipe: StaticMsgPipe,
    private _servicingCommonStoreService: ServicingCommonStoreService
  ) {
    super(_common);
  }

  /**
   * 座席指定状況
   * @param passengerId 搭乗者ID
   * @returns
   */
  getSeatNumber(passengerId: string) {
    let tempSeatNo = '';
    let tempSegment = this.selectedSeatInfoList?.find((s) => s.segmentId === this.displayTargetSegmentId);
    let tempPassenger = tempSegment?.passengerList.find((p) => p.id === passengerId);
    let tempSeat = tempPassenger?.seatNumberList?.find((s) => !!s.seatNumber);

    // 選択中座席情報.当該セグメント.当該選択している座席情報.座席番号≠””
    if (!!tempPassenger?.seatNumberList && tempPassenger.seatNumberList?.length > 0) {
      // 選択中座席情報.当該セグメント.当該選択している座席情報.座席番号リスト.座席番号[0]
      if (!!tempSeat?.seatNumber) {
        tempSeatNo = tempSeat?.seatNumber;
      }

      // 上記以外、かつPNR情報.data.seats.<当該flights>.<当該OrderSeatItems>. seatSelection.seatNumbers≠””
    } else if (
      !!this.displayTargetSegmentId &&
      !!this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[passengerId]?.seatSelection?.seatNumbers
    ) {
      // PNR情報.data.seats.<当該flights>.<当該OrderSeatItems>.seatSelection.seatNumbers
      tempSeatNo = this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[passengerId]?.seatSelection?.seatNumbers[0];
    }

    if (this.seatInformationMap?.get(tempSeatNo)?.isCouchSeat) {
      return this.seatInformationMap?.get(tempSeatNo)?.couchNumber;
    }

    return tempSeatNo;
  }

  /**
   * シート金額
   * @returns
   */
  getSeatPrice() {
    let totalprice = 0;

    /** 有料ASR席 */
    if (!!this.seatNumber && this.seatInformationMap?.get(this.seatNumber)?.isChargeableAsrSeat) {
      let tempPrice = this.seatInformationMap.get(this.seatNumber)?.prices?.total;
      if (!this.isInformative) {
        tempPrice = this.seatInformationMap
          .get(this.seatNumber)
          ?.travelers?.find((t) => t.id === this.selectingPassengerId)?.prices?.total;
      }

      if (!!tempPrice) {
        totalprice = tempPrice;
      }

      return totalprice;
    }

    if (!!this.displayTargetSegmentId && !!this.selectingPassengerId && !!this.couchSeatNumber) {
      /** カウチ席３人席・４人席 */
      if (this.getCouchNumberOfSeats(this.couchSeatNumber) === 3) {
        switch (this.couchSeatReservedPassengerCount) {
          case 1:
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue.threeSeats
                ?.oneTraveler?.catalogue?.prices?.total;
            return totalprice;
          case 2:
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue.threeSeats
                ?.twoTraveles?.catalogue?.prices?.total;
            return totalprice;
          case 3:
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue.threeSeats
                ?.threeTravelers?.catalogue?.prices?.total;
            return totalprice;
          default:
            break;
        }
      } else if (this.getCouchNumberOfSeats(this.couchSeatNumber) === 4) {
        switch (this.couchSeatReservedPassengerCount) {
          case 1:
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue?.fourSeats
                ?.oneTraveler?.catalogue?.prices?.total;
            return totalprice;
          case 2:
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue?.fourSeats
                ?.twoTraveles?.catalogue?.prices?.total;
            return totalprice;
          case 3:
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue?.fourSeats
                ?.threeTravelers?.catalogue?.prices?.total;
            return totalprice;
          case 4:
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue?.fourSeats
                ?.fourTravelers?.catalogue?.prices?.total;
            return totalprice;
          default:
            break;
        }
      }
    }

    return totalprice;
  }

  /**
   * カウチ席最安シート金額
   * @returns
   */
  getCouchSeatLowestPrice() {
    let totalprice = 0;

    if (!!this.displayTargetSegmentId && !!this.selectingPassengerId && !!this.couchSeatNumber) {
      /** カウチ席３人席・４人席 */
      if (this.getCouchNumberOfSeats(this.couchSeatNumber) === 3) {
        switch (true) {
          case 3 <= (this.pnrInfo?.travelers?.length || 0):
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue.threeSeats
                ?.threeTravelers?.catalogue?.prices?.total || 0;
            return totalprice;
          case 2 <= (this.pnrInfo?.travelers?.length || 0):
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue.threeSeats
                ?.twoTraveles?.catalogue?.prices?.total || 0;
            return totalprice;
          case 1 <= (this.pnrInfo?.travelers?.length || 0):
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue.threeSeats
                ?.oneTraveler?.catalogue?.prices?.total || 0;
            return totalprice;
          default:
            break;
        }
      } else if (this.getCouchNumberOfSeats(this.couchSeatNumber) === 4) {
        switch (true) {
          case 4 <= (this.pnrInfo?.travelers?.length || 0):
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue?.fourSeats
                ?.fourTravelers?.catalogue?.prices?.total || 0;
            return totalprice;
          case 3 <= (this.pnrInfo?.travelers?.length || 0):
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue?.fourSeats
                ?.threeTravelers?.catalogue?.prices?.total || 0;
            return totalprice;
          case 2 <= (this.pnrInfo?.travelers?.length || 0):
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue?.fourSeats
                ?.twoTraveles?.catalogue?.prices?.total || 0;
            return totalprice;
          case 1 <= (this.pnrInfo?.travelers?.length || 0):
            totalprice =
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[this.selectingPassengerId]?.couchCatalogue?.fourSeats
                ?.oneTraveler?.catalogue?.prices?.total || 0;
            return totalprice;
          default:
            break;
        }
      }
    }

    return totalprice;
  }

  /**
   * 当該搭乗者が有料ASR座席料金免除対象であるかどうか
   */
  hasFreePermission() {
    if (this.isInformative && !!this.seatNumber) {
      return !!this.seatInformationMap?.get(this.seatNumber)?.travelers?.find((t) => t.isExempted);
    } else {
      return !!this.seatNumber
        ? this.seatInformationMap?.get(this.seatNumber)?.travelers?.find((t) => t.id === this.selectingPassengerId)
            ?.isExempted
        : false;
    }
  }

  /**
   * カウチ席数
   * @param couchSeatNumber
   */
  getCouchNumberOfSeats(couchSeatNumber: string) {
    return (
      (!!this.seatmapInfo?.data?.seatmaps?.[couchSeatNumber] &&
        (this.seatmapInfo?.data?.seatmaps?.[couchSeatNumber].couchNumberOfSeats as number)) ||
      0
    );
  }

  /**
   * 搭乗者欄のクリックイベントハンドラ
   * 座席選択中の搭乗者を切り替える
   * @param id
   */
  onClickPassenger(id: string) {
    if (this.isUnchangeable(id)) {
      return;
    }
    // 2	画面.カウチ席利用搭乗者一覧.当該チェックあり=trueである場合、
    // 画面.カウチ席利用搭乗者一覧.当該搭乗者IDをチェック対象搭乗者IDとして保持する。
    this.selectingPassengerId = id;

    if (this.couchSeatAppliedPassengerList?.get(id)) {
      this.couchSeatAppliedPassengerList.set(id, false);
    } else {
      this.couchSeatAppliedPassengerList?.set(id, true);
    }

    // 3	画面.カウチ席利用搭乗者一覧.チェックあり=trueである要素数を、カウチ席指定中人数として保持する。
    this.couchSeatReservedPassengerCount = 0;
    this.couchSeatAppliedPassengerList?.forEach((value) => {
      if (value) {
        this.couchSeatReservedPassengerCount! += 1;
      }
    });

    // 4	カウチ席番号が存在する場合、以下の合計金額設定処理を行う。
    if (!!this.couchSeatNumber) {
      // 4.2	カウチ席指定中人数>0の場合、表示対象セグメントIDに紐づくPNR情報.data.seats.<当該flightId>.
      // <当該OrderSeatItems>.couchCatalogueをカウチ金額情報として取得する。
      if (this.couchSeatReservedPassengerCount > 0 && this.displayTargetSegmentId) {
        let couchCatalogue = this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[id]?.couchCatalogue;
        if (!!couchCatalogue) {
          this.couchAmountInfo = couchCatalogue;
        }
      }

      // 4.3	当該カウチ席.席数=3の場合、カウチ金額情報.threeSeats(以下、3人席情報とする)の内容を、
      // 以下に応じてチェック対象搭乗者ID.全搭乗者IDをキーに取得した選択中座席情報.当該選択している座席情報.指定金額に設定する。
      if (this.getCouchNumberOfSeats(this.couchSeatNumber) === 3) {
        switch (this.couchSeatReservedPassengerCount) {
          // 4.3.1	カウチ席指定中人数=1の場合、3人席情報.oneTraveler.prices.total
          case 1:
            this.selectedSeatInfoList.forEach((seatInfo) => {
              seatInfo.passengerList.forEach((passenger) => {
                if (passenger.id === id && !!this.couchAmountInfo?.threeSeats?.oneTraveler?.catalogue?.prices?.total) {
                  this.reservedCouchSeatTotalAmount =
                    this.couchAmountInfo?.threeSeats?.oneTraveler?.catalogue?.prices?.total;
                }
              });
            });
            break;
          // 4.3.2	カウチ席指定中人数=2の場合、3人席情報.twoTraveler.prices.total
          case 2:
            this.selectedSeatInfoList.forEach((seatInfo) => {
              seatInfo.passengerList.forEach((passenger) => {
                if (passenger.id === id && !!this.couchAmountInfo?.threeSeats?.twoTraveles?.catalogue?.prices?.total) {
                  this.reservedCouchSeatTotalAmount =
                    this.couchAmountInfo?.threeSeats?.twoTraveles?.catalogue?.prices?.total;
                }
              });
            });
            break;
          // 4.3.3	カウチ席指定中人数=3の場合、3人席情報.threeTraveler.prices.total
          case 3:
            this.selectedSeatInfoList.forEach((seatInfo) => {
              seatInfo.passengerList.forEach((passenger) => {
                if (
                  passenger.id === id &&
                  !!this.couchAmountInfo?.threeSeats?.threeTravelers?.catalogue?.prices?.total
                ) {
                  this.reservedCouchSeatTotalAmount =
                    this.couchAmountInfo?.threeSeats?.threeTravelers?.catalogue?.prices?.total;
                }
              });
            });
            break;
        }
      }

      // 4.4	当該カウチ席.席数=4の場合、カウチ金額情報.fourSeats(以下、4人席情報とする)の内容を、
      // 以下に応じてチェック対象搭乗者ID.全搭乗者IDをキーに取得した選択中座席情報.当該選択している座席情報.指定金額に設定する。
      if (this.getCouchNumberOfSeats(this.couchSeatNumber) === 4) {
        switch (this.couchSeatReservedPassengerCount) {
          // 4.4.1	カウチ席指定中人数=1の場合、4人席情報.oneTraveler.prices.total
          case 1:
            this.selectedSeatInfoList.forEach((seatInfo) => {
              seatInfo.passengerList.forEach((passenger) => {
                if (passenger.id === id && !!this.couchAmountInfo?.fourSeats?.oneTraveler?.catalogue?.prices?.total) {
                  this.reservedCouchSeatTotalAmount =
                    this.couchAmountInfo?.fourSeats?.oneTraveler?.catalogue?.prices?.total;
                }
              });
            });
            break;
          // 4.4.2	カウチ席指定中人数=2の場合、4人席情報.twoTraveler.prices.total
          case 2:
            this.selectedSeatInfoList.forEach((seatInfo) => {
              seatInfo.passengerList.forEach((passenger) => {
                if (passenger.id === id && this.couchAmountInfo?.fourSeats?.twoTraveles?.catalogue?.prices?.total) {
                  this.reservedCouchSeatTotalAmount =
                    this.couchAmountInfo?.fourSeats?.twoTraveles?.catalogue?.prices?.total;
                }
              });
            });
            break;
          // 4.4.3	カウチ席指定中人数=3の場合、4人席情報.threeTraveler.prices.total
          case 3:
            this.selectedSeatInfoList.forEach((seatInfo) => {
              seatInfo.passengerList.forEach((passenger) => {
                if (passenger.id === id && this.couchAmountInfo?.fourSeats?.threeTravelers?.catalogue?.prices?.total) {
                  this.reservedCouchSeatTotalAmount =
                    this.couchAmountInfo?.fourSeats?.threeTravelers?.catalogue?.prices?.total;
                }
              });
            });
            break;
          // 4.4.4	カウチ席指定中人数=4の場合、4人席情報.fourTraveler.prices.total
          case 4:
            this.selectedSeatInfoList.forEach((seatInfo) => {
              seatInfo.passengerList.forEach((passenger) => {
                if (passenger.id === id && this.couchAmountInfo?.fourSeats?.fourTravelers?.catalogue?.prices?.total) {
                  this.reservedCouchSeatTotalAmount =
                    this.couchAmountInfo?.fourSeats?.fourTravelers?.catalogue?.prices?.total;
                }
              });
            });
            break;
        }
      }

      // 4.6	カウチ席指定人数=0の場合、画面.カウチ席指定料金合計金額に0を設定する。
      if (this.couchSeatReservedPassengerCount === 0) {
        this.reservedCouchSeatTotalAmount = 0;
      }
    }
    // 5	上記以外の場合、未指定の旨
    else {
      this.reservedCouchSeatTotalAmount = undefined;
    }
  }

  /**
   * 座席指定済みかどうか
   * @param passengerId 搭乗者ID
   * @returns
   */
  isSelected(passengerId: string) {
    return this.couchSeatAppliedPassengerList.get(passengerId);
  }

  /**
   * 搭乗者毎に該当するボタンの読み上げ文言を取得。
   * @param id 搭乗者ID
   * @returns 該当する静的文言キー
   */
  getButtonReaderStaticMsgKey(id: string): string {
    if (this.isUnchangeable(id)) {
      // 座席変更不可の場合
      return 'reader.notSelectableCouchiiSeatsPassenger';
    } else if (this.isSelected(id)) {
      // 座席を指定済みの場合
      return 'reader.selectingCouchiiSeatsPassenger';
    } else if (!!this.selectedSeatInfoList && !!this.displayTargetSegmentId) {
      let selectedSeatInfo = this.selectedSeatInfoList.find((info) => info.segmentId === this.displayTargetSegmentId);
      let seatInfoPassenger = selectedSeatInfo?.passengerList?.find((passenger) => passenger.id === id);
      if (
        !!seatInfoPassenger &&
        ((!!seatInfoPassenger.seatNumberList && seatInfoPassenger.seatNumberList.length > 0) ||
          (!!seatInfoPassenger.couchSeatNumberList &&
            seatInfoPassenger.couchSeatNumberList.length > 0 &&
            !!seatInfoPassenger.couchSeatNumberList[0].seatNumber))
      ) {
        // 座席を指定済みの場合
        return 'reader.selectableCouchiiSeatsPassenger';
      }
    }
    // 座席未指定の場合
    return 'reader.selectableCouchiiSeatsPassenger';
  }

  /**
   * 指定する（有料席詳細確認モーダル）のクリックイベント
   */
  onSelectClick() {
    // 1.2	カウチ席指定取消同時操作かどうかにfalse(同時操作なし)を設定する。
    let isCancelReserveCouchSeat = false;
    // 1.5	カウチ席指定対象搭乗者IDリスト
    let _couchSeatAppliedPassengerList: string[] = [];
    // 1.6	カウチ席指定対象外搭乗者IDリスト
    let _notCouchSeatAppliedPassengerList: string[] = [];

    if (!!this.displayTargetSegmentId) {
      // 2	当該座席.カウチ席かどうか=trueの場合、カウチ席指定変更処理として、以下の処理を行う。
      if (!!this.seatNumber && this.seatInformationMap?.get(this.seatNumber)?.isCouchSeat) {
        //2.1	カウチ席申込可否判定処理として、以下の処理を行う。

        //2.1.1	画面.カウチ席利用搭乗者一覧.チェックあり=trueである搭乗者のIDを、カウチ席指定対象搭乗者リストに追加する。
        this.couchSeatAppliedPassengerList?.forEach((value, key) => {
          if (value) {
            _couchSeatAppliedPassengerList.push(key);
          } else {
            //2.1.2	画面.カウチ席利用搭乗者一覧.チェックあり=falseである搭乗者を、カウチ席指定対象外搭乗者リストとして保持する。
            _notCouchSeatAppliedPassengerList.push(key);
          }
        });

        //2.1.3	カウチ席指定取消同時操作かどうか判定処理として、以下の処理を行う。

        // ＜以下、カウチ席指定対象搭乗者IDリストの要素数分、繰り返し＞
        for (const couchPassengerId of _couchSeatAppliedPassengerList) {
          // 2.1.3.1	カウチ席指定対象搭乗者IDリスト.当該搭乗者IDに紐づくPNR情報.data.seats.＜表示対象セグメントID＞.
          // ＜当該搭乗者ID＞が存在しない場合、以下の処理を行う。
          if (!this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[couchPassengerId]) {
            // ＜以下、カウチ席指定対象外搭乗者リストの要素数分、繰り返し＞
            _notCouchSeatAppliedPassengerList.forEach((notCouchPassengerId) => {
              if (!!this.displayTargetSegmentId && !!this.seatNumber) {
                // 2.1.3.1.1	カウチ席指定対象外搭乗者リスト.当該搭乗者IDに紐づくPNR情報.data.seats.＜表示対象セグメントID＞.
                // ＜当該搭乗者ID＞.seatSelection.couchSeatNumber=座席番号の場合、カウチ席指定取消同時操作かどうかにtrueを設定する。
                if (
                  this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[notCouchPassengerId]?.seatSelection
                    .couchSeatNumber === this.couchSeatNumber
                ) {
                  isCancelReserveCouchSeat = true;
                }
              }
            });

            // 2.1.3.2	上記以外かつカウチ席指定対象搭乗者IDリスト.当該搭乗者IDに紐づくPNR情報.data.seats.＜表示対象セグメントID＞.
            // ＜当該搭乗者ID＞.seatSelection.couchSeatNumber=当該座席座席番号である場合、カウチ席指定取消同時操作かどうかにtrueを設定する。
          } else if (
            this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[couchPassengerId]?.seatSelection.couchSeatNumber ===
            this.couchSeatNumber
          ) {
            isCancelReserveCouchSeat = true;
          }

          // 2.1.3.3	カウチ席指定取消同時操作かどうか=trueの場合、繰り返し処理を終了する。
          if (isCancelReserveCouchSeat) {
            break;
          }
        } // ＜ここまで、カウチ席指定対象外搭乗者リストの要素数分、繰り返し＞

        // 2.1.4	カウチ席指定取消同時操作かどうか=trueの場合、エラーメッセージID="E0739"
        // (カウチ席の取消と座席の指定は同時に行えない旨)にて継続可能なエラー情報を指定し、処理を終了する。
        if (isCancelReserveCouchSeat) {
          this._common.errorsHandlerService.setRetryableError(PageType.PAGE, { errorMsgId: 'E0739' });
          // 有料席詳細モーダルを閉じる。
          this.close();
          return;
        }

        // 2.1.5	カウチ席申込人数制限超過かどうかおよびカウチ席安全規定未満かどうかの判定処理として、以下の処理を行う。

        // 2.1.5.1	判定基準の初期化として以下の処理を行う。

        //  2.1.5.1.1	カウチ席申込人数カウントに0を設定する。
        let couchSeatAppliedPassengerCount = 0;
        //  2.1.5.1.2	同伴者存在有無としてfalseを保持する。
        let isCompanionExist = false;
        //  搭乗者マップ.＜当該搭乗者ID＞.搭乗者種別="ADT"、または"B15"（大人）の人数として0を設定する。
        let adultCount = 0;
        //  2.1.5.1.4	幼児人数として0を設定する。
        let infantCount = 0;
        //  席なし幼児人数として0を設定する。
        let infantWithoutSeatCount = 0;

        // ＜以下、カウチ席指定対象搭乗者IDリストの要素数分、繰り返し＞
        _couchSeatAppliedPassengerList.forEach((couchPassengerId) => {
          // カウチ席指定対象搭乗者リスト.当該値を、チェック対象搭乗者IDとして保持する。
          let checkingPassengerId = couchPassengerId;
          let checkingPassenger = this.travelersMap?.get(checkingPassengerId);

          // 搭乗者マップ.＜当該搭乗者ID＞.搭乗者種別="INF"以外の場合、カウチ席申込人数カウントをインクリメントする。
          // ※搭乗者種別="INF" の場合、accompanyingInfant に設定される認識なので基本的には通らない認識
          if (checkingPassenger?.passengerTypeCode !== 'INF') {
            couchSeatAppliedPassengerCount += 1;
          }

          // 搭乗者マップ.＜当該搭乗者ID＞.搭乗者種別="ADT"、または"B15"の場合、同伴者存在有無にtrueを設定し、大人またはヤングアダルトの搭乗者人数をインクリメントする。
          if (checkingPassenger?.passengerTypeCode === 'ADT' || checkingPassenger?.passengerTypeCode === 'B15') {
            isCompanionExist = true;
            adultCount += 1;
          }

          // 以下のいずれかの条件に該当する場合、席なし幼児人数をインクリメントする。
          // ・搭乗者マップ.＜当該搭乗者ID＞.同伴幼児情報が存在する場合
          // ・搭乗者マップ.＜当該搭乗者ID＞.幼児情報不明=trueの場合
          // ・搭乗者マップ.＜当該搭乗者ID＞.幼児同伴の大人=trueの場合
          if (
            !!checkingPassenger?.accompanyingInfant ||
            checkingPassenger?.hasNamelessInfant ||
            checkingPassenger?.hasInfiniNamelessInfant
          ) {
            infantWithoutSeatCount += 1;
          }

          // 以下のいずれかの条件に該当する場合、幼児人数をインクリメントする。
          // ・搭乗者マップ.＜当該搭乗者ID＞.同伴幼児情報が存在する場合
          // ・搭乗者マップ.＜当該搭乗者ID＞.搭乗者種別="INS"の場合
          // ・搭乗者マップ.＜当該搭乗者ID＞.幼児情報不明=trueの場合
          // ・搭乗者マップ.＜当該搭乗者ID＞.幼児同伴の大人=trueの場合
          if (
            !!checkingPassenger?.accompanyingInfant ||
            checkingPassenger?.passengerTypeCode === 'INS' ||
            checkingPassenger?.hasNamelessInfant ||
            checkingPassenger?.hasInfiniNamelessInfant
          ) {
            infantCount += 1;
          }
        }); // ＜ここまで、カウチ席指定対象搭乗者リストの要素数分、繰り返し＞

        // 申込人数制限フラグ
        let isLimitNumberOfApplicants = false;
        // 安全規定制限フラグ
        let isSafetyRegulationLimitations = false;

        if (!!this.couchSeatNumber) {
          let couchNumberOfSeats = this.getCouchNumberOfSeats(this.couchSeatNumber) || 0;
          // 以下のいずれかに該当する場合、申込人数制限フラグに true を設定し、“E0582”（申込人数に制限がある旨）を表示する。
          // ・当該カウチ席を指定する搭乗者のうち、席なし幼児を除くカウチ席搭乗者の人数 > 当該座席.カウチ席数(3 or 4)の場合
          // ・当該カウチ席を指定する搭乗者のうち、席なし幼児を含む登場者の総人数 > 当該座席.カウチの座席数(3 or 4) + 2の場合
          if (
            couchSeatAppliedPassengerCount > couchNumberOfSeats ||
            couchSeatAppliedPassengerCount + infantWithoutSeatCount > couchNumberOfSeats + 2
          ) {
            isLimitNumberOfApplicants = true;

            this._common.errorsHandlerService.setRetryableError(PageType.PAGE, { errorMsgId: 'E0582' });
          }

          // 以下のいずれかに該当する場合、安全規定制限フラグに true を設定し、”E0583”（安全規定の制限がある旨）を表示する。
          // ・幼児が存在する、かつ搭乗者種別が大人またはヤングアダルトの搭乗者が存在しない場合
          // ・大人の旅客1人に対して3人以上の幼児が存在する（カウチ席内人数(大人・ヤングアダルト)×2 < 幼児人数）場合
          if ((infantCount > 0 && !isCompanionExist) || adultCount * 2 < infantCount) {
            isSafetyRegulationLimitations = true;

            this._common.errorsHandlerService.setRetryableError(PageType.PAGE, { errorMsgId: 'E0583' });
          }
        }

        // 申込人数制限フラグ、安全規定制限フラグがいずれもfalseである場合、カウチ席指定変更処理終了のため、以下の処理を行う。
        if (!isLimitNumberOfApplicants && !isSafetyRegulationLimitations) {
          let isBassinetSeat = false;
          // ＜以下、カウチ席指定対象搭乗者リストの要素数分、繰り返し表示＞
          for (const passengerId of _couchSeatAppliedPassengerList) {
            // 2.3.3	表示対象セグメントID、カウチ席指定対象搭乗者リスト.当該搭乗者IDをキー名とする、
            // PNR情報.data.seats.＜当該表示対象セグメントID＞.＜当該搭乗者ID＞.bassinet=trueの場合、以下の処理を行う。
            if (!!this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[passengerId]?.bassinet) {
              // 2.3.3.1	以下のパラメータにて確認ダイアログ(G02-005)を表示する。
              //type:	"choice"(選択ダイアログ)
              //message:	“MSG0638”(バシネットを削除する旨)
              // 2.3.3.2	確認ダイアログ(G02-005)よりキャンセルボタン押下イベントが返却された場合、指定する（有料席詳細確認モーダル）ボタン押下処理を終了する。
              // 2.3.3.3	確認ダイアログ(G02-005)より確認ボタン押下イベントが返却された場合、処理を継続する。
              this._dialogDisplayService
                .openDialog({
                  size: DialogSize.L,
                  type: DialogType.CHOICE,
                  message: `${MasterJsonKeyPrefix.DYNAMIC}MSG0638`,
                })
                .buttonClick$.subscribe((result) => {
                  if (result.clickType === DialogClickType.CONFIRM) {
                    this.setPaidSeatInfo(_couchSeatAppliedPassengerList);

                    // 2.3.2	有料席詳細モーダルを閉じる。
                    this.close();
                    return;
                  } else if (result.clickType === DialogClickType.CLOSE) {
                  }
                });

              isBassinetSeat = true;
              // 2.3.3.4	当繰り返し処理を終了する。
              break;
            }
          } // ＜ここまで、カウチ席指定対象搭乗者リストの要素数分、繰り返し表示＞

          if (isBassinetSeat) {
            return;
          }

          // エラーが存在する場合
        } else if (isLimitNumberOfApplicants || isSafetyRegulationLimitations) {
          // 有料席詳細モーダルを閉じる。
          this.close();

          return;
        }
        // 3	上記以外の場合、有料ASR席指定処理として以下の処理を実施する。
      } else {
        // ＜以下、搭乗者IDリストの要素数分、繰り返し＞
        for (let passengerId of this.passengerIdList) {
          // 3.1	搭乗者IDリスト.当該搭乗者ID=選択中搭乗者IDの場合、次の繰り返しを行う。
          if (passengerId === this.selectingPassengerId) {
            continue;
            // 3.2	上記以外の場合、他搭乗者が当該座席をSSR登録済か判定するため、以下の処理を行う。
          } else {
            // 3.2.1	表示対象セグメントID、搭乗者IDリスト.当該搭乗者IDをキーとする、PNR情報.data.seats.＜表示対象セグメントID＞.
            // ＜当該搭乗者ID＞.selectSeats.seatNumbers=座席情報.当該座席番号の場合、以下の処理を行う。
            if (
              this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[passengerId]?.seatSelection.seatNumbers.includes(
                this.seatNumber
              )
            ) {
              // 3.2.1.1	エラーメッセージID="E0738"(有料ASR席の取消と座席の指定は同時に行えない旨)とし、継続可能なエラー情報を指定する。
              this._common.errorsHandlerService.setRetryableError(PageType.PAGE, { errorMsgId: 'E0738' });

              // 3.2.1.2	指定する（有料席詳細確認モーダル）ボタン押下処理を終了し、有料席詳細確認モーダルを閉じる。
              this.close();
              return;
            }
          }
        } // ＜ここまで、搭乗者IDリストの要素数分、繰り返し＞
      }

      // 有料席を指定する。
      this.setPaidSeatInfo(_couchSeatAppliedPassengerList);
    }

    // 5	有料席詳細確認モーダルを閉じる。
    this.close();
  }

  /**
   * 有料席を指定する。
   * @param _couchSeatAppliedPassengerList カウチ席指定対象搭乗者IDリスト
   */
  setPaidSeatInfo(_couchSeatAppliedPassengerList: string[]) {
    //4	選択中座席情報リスト更新処理として、以下の処理を行う。
    this.setSelectedSeatInfo(_couchSeatAppliedPassengerList);

    // 有料ASR席・カウチ席新規申込有無にtrueを設定する。
    // 全セグメント対象座席指定状況変更フラグにtrueを設定する。
    // セグメント別座席指定状況変更フラグにtrueに設定する。

    this._servicingSeatmapStoreService.updateServicingSeatmap({
      isAllSegmentStateChange: true,
      isPaidASRCouchNewApplicationAvailable: true,
      isSeatReservationStatusChangeBySegment: true,
      selectedSeatInfoList: this.selectedSeatInfoList,
    });
  }

  /**
   * 選択中座席情報リスト更新
   * @param _couchSeatAppliedPassengerList カウチ席指定対象搭乗者IDリスト
   */
  setSelectedSeatInfo(_couchSeatAppliedPassengerList: string[]) {
    if (!!this.seatNumber && !!this.selectingPassengerId && !!this.displayTargetSegmentId) {
      // カウチ席の場合の処理
      if (!!this.seatInformationMap?.get(this.seatNumber)?.isCouchSeat && !!this.couchSeatNumber) {
        // 以下、カウチ席指定対象搭乗者IDリストの分繰り返し

        // チェックを外されたものを除外
        this.selectedSeatInfoList
          .find((seg) => seg.segmentId === this.displayTargetSegmentId)
          ?.passengerList?.forEach((passenger) => {
            // NOTE：更新判定時のチェックは別途行っているのでここで更新時のチェックは不要
            if (!this.isUnchangeable(passenger.id) && !_couchSeatAppliedPassengerList.find((p) => p === passenger.id)) {
              const newSeatInfoList = this.selectedSeatInfoList.map((seatInfo) => {
                const newPassengerList = seatInfo.passengerList.map((value) => {
                  if (
                    seatInfo.segmentId !== this.displayTargetSegmentId ||
                    !value.seatNumberList?.find((s) => s.seatNumber === this.seatNumber)
                  ) {
                    return value;
                  }

                  return {
                    id: value.id,
                    seatNumberList: [],
                    couchSeatNumberList: [],
                    ssrInformation: undefined,
                    specifiedAmount: 0,
                    hasBassinetAttribute: value.hasBassinetAttribute,
                    isChildSeatSelectable: value.isChildSeatSelectable,
                    isSelectable: true,
                  };
                });
                return {
                  segmentId: seatInfo.segmentId,
                  numberOfChildSeats: seatInfo.numberOfChildSeats,
                  passengerList: newPassengerList,
                  characteristicRequestSsrCode: seatInfo.characteristicRequestSsrCode || '',
                };
              });

              this.selectedSeatInfoList = newSeatInfoList;
            }
          });

        // 選択された座席を設定
        _couchSeatAppliedPassengerList.forEach((passengerId) => {
          if (!!this.selectedSeatInfoList) {
            const newSeatInfoList = this.selectedSeatInfoList.map((seatInfo) => {
              const newPassengerList = seatInfo.passengerList.map((value) => {
                if (seatInfo.segmentId !== this.displayTargetSegmentId || value.id !== passengerId) {
                  return value;
                }

                return {
                  id: value.id,
                  seatNumberList: this.getSeatNumberFromCouch(),
                  couchSeatNumberList: [{ seatNumber: this.couchSeatNumber }],
                  ssrInformation: undefined,
                  specifiedAmount: this.getSeatPrice(),
                  hasBassinetAttribute: value.hasBassinetAttribute,
                  isChildSeatSelectable: value.isChildSeatSelectable,
                  isSelectable: false,
                } as SeatInfoPassengers;
              });
              return {
                segmentId: seatInfo.segmentId,
                numberOfChildSeats: seatInfo.numberOfChildSeats,
                passengerList: newPassengerList,
                characteristicRequestSsrCode: seatInfo.characteristicRequestSsrCode || '',
              };
            });

            this.selectedSeatInfoList = newSeatInfoList;
          }
        });

        // 有料ASR席の場合の処理
      } else if (!!this.seatInformationMap?.get(this.seatNumber)?.isChargeableAsrSeat) {
        const newSeatInfoList = this.selectedSeatInfoList.map((seatInfo) => {
          const newPassengerList = seatInfo.passengerList.map((passengerInfo) => {
            if (seatInfo.segmentId !== this.displayTargetSegmentId || passengerInfo.id !== this.selectingPassengerId) {
              return passengerInfo;
            }

            return {
              id: passengerInfo.id,
              seatNumberList: [{ seatNumber: this.seatNumber }],
              couchSeatNumberList: [],
              ssrInformation: undefined,
              specifiedAmount: this.getSeatPrice(),
              hasBassinetAttribute: passengerInfo.hasBassinetAttribute,
              isChildSeatSelectable: passengerInfo.isChildSeatSelectable,
              isSelectable: false,
            } as SeatInfoPassengers;
          });
          return {
            segmentId: seatInfo.segmentId,
            numberOfChildSeats: seatInfo.numberOfChildSeats,
            passengerList: newPassengerList,
            characteristicRequestSsrCode: seatInfo.characteristicRequestSsrCode || '',
          };
        });

        this.selectedSeatInfoList = newSeatInfoList;
      }
    }
  }

  /**
   * 座席選択済みかどうか
   * @param passengerId
   * @returns
   */
  isUnchangeable(passengerId: string): boolean {
    if (!!this.displayTargetSegmentId) {
      let pnrSeat = !!this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[passengerId]
        ? this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[passengerId].seatSelection.seatNumbers[0]
        : undefined;

      if (
        !!pnrSeat &&
        (this.seatInformationMap?.get(pnrSeat)?.isCouchSeat ||
          this.seatInformationMap?.get(pnrSeat)?.isChargeableAsrSeat)
      ) {
        return true;
      }

      if (!!this.seatNumber) {
        // 座席利用不可の場合
        const seatAvailabilityStatus = this.seatInformationMap
          ?.get(this.seatNumber)
          ?.travelers?.find((t) => t.id === passengerId)?.seatAvailabilityStatus;
        if (!!seatAvailabilityStatus && seatAvailabilityStatus !== 'available') {
          return true;
        }
      }

      // 選択中の旅客に紐づくカウチのEMDが存在する場合
      const hasCouchSeatEMD: boolean =
        this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[passengerId].seatSelection?.hasCouchSeatEMD || false;
      if (hasCouchSeatEMD) {
        return true;
      }
    }
    return false;
  }

  /**
   * カウチ席番号取得
   */
  getSeatNumberFromCouch(): SeatNumbers[] {
    if (!!this.couchSeatNumber) {
      const { rowNumber, columnNumbers } = splitCouchSeatNumber(this.couchSeatNumber);

      if (this.getCouchNumberOfSeats(this.couchSeatNumber) === 3) {
        return [
          { seatNumber: `${rowNumber}${columnNumbers?.[0]}` },
          { seatNumber: `${rowNumber}${columnNumbers?.[1]}` },
          { seatNumber: `${rowNumber}${columnNumbers?.[2]}` },
        ];
      } else {
        return [
          { seatNumber: `${rowNumber}${columnNumbers?.[0]}` },
          { seatNumber: `${rowNumber}${columnNumbers?.[1]}` },
          { seatNumber: `${rowNumber}${columnNumbers?.[2]}` },
          { seatNumber: `${rowNumber}${columnNumbers?.[3]}` },
        ];
      }
    }

    return [];
  }

  /**
   * シートマップ情報の表示判定
   * @param seats シートマップ情報
   * @returns 判定結果
   */
  private _isDisplay(seats: SeatmapStoreModel | undefined) {
    if (seats && seats.segments && seats.seatmap && seats.seatNumber) {
      this.isInformative = seats.isInformative || false;
      if (this.isInformative) {
        // シートマップ参照の場合
        return true;
      } else {
        // シートマップの場合
        if (
          seats.passengers &&
          seats.passengerList &&
          this._servicingCommonStoreService.servicingCommon.displayTargetSegmentId &&
          seats.selectedSeatInfoList &&
          seats.pnrInfo
        ) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * 席なし幼児名取得
   * @param passengerId 搭乗者ID
   * @returns
   */
  getInfantName(passengerId?: string) {
    let infantName = '';
    if (!!passengerId && !!this.travelersMap?.get(passengerId)?.accompanyingInfant) {
      infantName = this.travelersMap.get(passengerId)?.accompanyingInfant?.name || '';
    }

    return infantName;
  }
}
